import React, { useState, useEffect } from 'react';
import {
  Building2, Users, DollarSign, Percent, TrendingUp,
  ArrowUpRight, ArrowDownRight, Search, Bell, Filter,
  MoreVertical, Plus, Building, UserCheck, Key, ShieldAlert,
  Home, Calendar, Clock, AlertCircle, CheckCircle, XCircle,
  Wallet, Banknote, Landmark, Briefcase, PieChart,
  TrendingDown, Activity, BarChart3, LineChart,
  Layers, MapPin, Globe, BriefcaseBusiness
} from 'lucide-react';

const AnimatedCounter = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState('0');
  useEffect(() => {
    const numericVal = parseFloat(value.replace(/[^0-9.-]/g, ''));
    if (isNaN(numericVal)) { setDisplay(value); return; }
    let start = 0;
    const step = Math.ceil(numericVal / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= numericVal) { setDisplay(value); clearInterval(timer); return; }
      const formatted = numericVal >= 1000 ? start.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : start.toFixed(2);
      setDisplay(value.replace(/[0-9,.-]+/, formatted));
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
};

const ProgressBar = ({ width, color = 'from-blue-300 to-indigo-400', delay = 0, height = 'h-1.5' }) => (
  <div className={`w-full ${height} bg-indigo-50 rounded-full overflow-hidden`}>
    <div
      className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-1000 ease-out`}
      style={{ width: `${Math.min(width, 100)}%`, transitionDelay: `${delay}ms` }}
    />
  </div>
);

const FadeInCard = ({ children, delay = 0, className = '' }) => (
  <div
    className={`animate-fadeInUp ${className}`}
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
  >
    {children}
  </div>
);

export default function RSDashboard() {
  const [timeframe, setTimeframe] = useState('Today');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('ALL');

  // TOPLINE Metrics - Real Estate Focused
  const toplineMetrics = [
    { label: 'ACTIVE LISTINGS', value: '142', change: '+12 this week', positive: true, icon: Building2, gradient: 'from-emerald-200 to-teal-300' },
    { label: 'PROPERTIES SOLD', value: '23', change: '+18.5% vs last month', positive: true, icon: Home, gradient: 'from-blue-200 to-indigo-300' },
    { label: 'TOTAL REVENUE', value: '$2.4M', change: '▲ 22.3% YTD', positive: true, icon: DollarSign, gradient: 'from-violet-200 to-purple-300' },
    { label: 'AVG SALE PRICE', value: '$485K', subLabel: 'Residential', change: '▲ 8.2% YoY', positive: true, icon: TrendingUp, gradient: 'from-amber-200 to-orange-300' },
    { label: 'COMMISSIONS', value: '$182K', subLabel: 'MTD earnings', change: '▲ 15.4% vs target', positive: true, icon: Wallet, gradient: 'from-emerald-200 to-green-300' },
    { label: 'PENDING DEALS', value: '18', subLabel: '$4.2M pipeline', change: '6 closing soon', positive: true, icon: Clock, gradient: 'from-cyan-200 to-blue-300' },
    { label: 'LEADS GENERATED', value: '324', change: '▲ 32.1% this month', positive: true, icon: Users, gradient: 'from-indigo-200 to-violet-300' },
    { label: 'CONVERSION RATE', value: '7.1%', change: '▲ 1.2% improvement', positive: true, icon: PieChart, gradient: 'from-rose-200 to-pink-300' },
  ];

  // Financial Performance - Real Estate Data
  const financialMetrics = {
    revenue: { value: '2,450,000', change: '+22.3% vs last quarter', negative: false },
    grossMargin: { value: '18.5%', label: 'Gross Profit Margin', change: '+2.1% improvement', negative: false },
    targetVsActual: { value: '$850K', label: 'Monthly Target', change: '$1.2M Achieved' },
    salesForecast: { value: '$12.8M' }
  };

  // Property Locations / Communities
  const branches = [
    { code: 'DOW', name: 'DOWNTOWN', amount: '12', count: '$3.2M', color: 'bg-blue-300' },
    { code: 'WTR', name: 'WATERFRONT', amount: '8', count: '$2.1M', color: 'bg-indigo-300' },
    { code: 'SUB', name: 'SUBURBAN', amount: '24', count: '$1.8M', color: 'bg-violet-300' },
    { code: 'LUX', name: 'LUXURY', amount: '5', count: '$4.5M', color: 'bg-purple-300' },
    { code: 'COM', name: 'COMMERCIAL', amount: '3', count: '$2.8M', color: 'bg-pink-300' },
    { code: 'RES', name: 'RESIDENTIAL', amount: '35', count: '$5.2M', color: 'bg-rose-300' },
    { code: 'NEW', name: 'NEW BUILD', amount: '15', count: '$3.8M', color: 'bg-orange-300' },
    { code: 'VAC', name: 'VACANT', amount: '8', count: '$0.6M', color: 'bg-amber-300' },
    { code: 'REN', name: 'RENOVATION', amount: '6', count: '$1.2M', color: 'bg-yellow-300' },
    { code: 'PRE', name: 'PREMIUM', amount: '4', count: '$6.1M', color: 'bg-lime-300' },
  ];

  // Chart data for net amount trend
  const trendData = [12, 18, 24, 30, 28, 32, 26, 34, 30, 36, 32, 38, 34, 40, 36, 42, 38, 44, 40, 46, 42, 48, 44, 50];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-violet-50/20 text-slate-800 font-sans antialiased flex">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        .gradient-animate {
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
        }
        .card-hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.12);
        }
        .metric-icon-pulse {
          transition: all 0.3s ease;
        }
        .card-hover-lift:hover .metric-icon-pulse {
          transform: scale(1.15) rotate(-3deg);
        }
        .branch-btn {
          transition: all 0.2s ease;
          position: relative;
        }
        .branch-btn.active {
          background: linear-gradient(135deg, #a5b4fc, #c4b5fd);
          color: #312e81;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(165, 180, 252, 0.3);
        }
        .branch-btn.active .branch-dot {
          background: white !important;
        }
        .time-btn {
          transition: all 0.2s ease;
        }
        .time-btn.active {
          background: linear-gradient(135deg, #a5b4fc, #c4b5fd);
          color: #312e81;
          box-shadow: 0 4px 12px rgba(165, 180, 252, 0.3);
        }
        .chart-bar {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .chart-bar:hover {
          transform: scaleY(1.1) translateY(-2px);
          opacity: 0.9;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-6 lg:p-8 space-y-6 overflow-y-auto">

          {/* Header - TOPLINE */}
          <FadeInCard delay={0}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-slate-700 via-slate-500 to-indigo-600 bg-clip-text text-transparent">
                   <span className="text-2xl font-medium text-slate-900 ml-2">Real Estate Dashboard</span>
                </h1>
              </div>
              {/* <div className="flex items-center gap-3">
                <button className="group inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-indigo-300 hover:text-indigo-600 hover:shadow-md transition-all duration-300">
                  <Filter size={16} />
                  Filter
                </button>
                <button className="group inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-300 to-violet-300 text-indigo-800 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-indigo-300/30 transition-all duration-300">
                  <Plus size={16} />
                  Add Listing
                </button>
              </div> */}
            </div>
          </FadeInCard>

          {/* TOPLINE Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {toplineMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              return (
                <FadeInCard key={idx} delay={50 + idx * 30}>
                  <div
                    className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-xl p-3 shadow-sm card-hover-lift overflow-hidden relative group"
                    onMouseEnter={() => setHoveredCard(`topline-${idx}`)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r ${metric.gradient}`} />
                    <div className="relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider truncate">{metric.label}</p>
                          <p className="text-sm font-extrabold text-slate-900 truncate mt-0.5">
                            <AnimatedCounter value={metric.value} duration={800} />
                          </p>
                          {metric.subLabel && (
                            <p className="text-[9px] font-semibold text-slate-600 truncate">{metric.subLabel}</p>
                          )}
                          {metric.change && (
                            <p className={`text-[8px] font-bold ${metric.positive ? 'text-emerald-600' : 'text-rose-600'} truncate flex items-center gap-0.5`}>
                              {metric.positive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                              {metric.change}
                            </p>
                          )}
                        </div>
                    <div className={`p-1.5 bg-gradient-to-br ${metric.gradient} rounded-lg shrink-0 ml-1`}>
                      <Icon size={12} className="text-indigo-800" />
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInCard>
              );
            })}
          </div>

          {/* Financial Performance Section */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <FadeInCard delay={350} className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-5 shadow-sm card-hover-lift">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">TOTAL REVENUE</p>
                  <p className="text-2xl font-extrabold text-slate-700">
                    <AnimatedCounter value={financialMetrics.revenue.value} />
                  </p>
                  <p className="text-xs font-medium text-slate-500">Year to date</p>
                  <p className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                    <TrendingUp size={14} />
                    {financialMetrics.revenue.change}
                  </p>
                </div>
              </div>
            </FadeInCard>

            <FadeInCard delay={430} className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-5 shadow-sm card-hover-lift">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">PROFIT MARGIN</p>
                  <p className="text-2xl font-extrabold text-slate-700">
                    <AnimatedCounter value={financialMetrics.grossMargin.value} />
                  </p>
                  <p className="text-xs font-medium text-slate-500">{financialMetrics.grossMargin.label}</p>
                  <p className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                    <TrendingUp size={14} />
                    {financialMetrics.grossMargin.change}
                  </p>
                </div>
              </div>
            </FadeInCard>

            <FadeInCard delay={510} className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-5 shadow-sm card-hover-lift">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">TARGET VS ACTUAL SALES</p>
                  <p className="text-2xl font-extrabold text-slate-700">
                    <AnimatedCounter value={financialMetrics.targetVsActual.value} />
                  </p>
                  <p className="text-xs font-medium text-slate-500">{financialMetrics.targetVsActual.label}</p>
                  <p className="text-xs font-semibold text-amber-600">{financialMetrics.targetVsActual.change}</p>
                </div>
              </div>
            </FadeInCard>

            <FadeInCard delay={590} className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-5 shadow-sm card-hover-lift">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ANNUAL REVENUE FORECAST</p>
                  <p className="text-2xl font-extrabold text-emerald-600">
                    <AnimatedCounter value={financialMetrics.salesForecast.value} />
                  </p>
                  <div className="flex items-end gap-1 pt-1">
                    {[65, 45, 55, 35, 50, 60, 40, 55, 45, 35, 50, 40, 55, 45, 60, 50, 40, 55, 45, 35].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm bg-gradient-to-t from-emerald-200 to-teal-300"
                        style={{ height: `${h * 0.3}px`, opacity: 0.5 + (h / 100) * 0.5 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </FadeInCard>
          </div>

          {/* Property Locations Section */}
          <FadeInCard delay={670}>
            <div className="bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-5 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-gradient-to-b from-blue-300 to-indigo-400 rounded-full" />
                    PROPERTY LOCATIONS · LIVE
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">Last updated 09:56</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">TOTAL INVENTORY</span>
                  <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <TrendingUp size={14} />
                    120 Properties
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400">For Sale</span>
                    <span className="text-sm font-bold text-slate-900">85</span>
                  </div>
                </div>
                <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400">For Rent</span>
                    <span className="text-sm font-bold text-slate-900">35</span>
                  </div>
                </div>
                <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400">Avg Days on Market</span>
                    <span className="text-sm font-bold text-slate-900">24</span>
                  </div>
                </div>
                <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400">Total Sq Ft</span>
                    <span className="text-sm font-bold text-slate-900">185K</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                <button className={`time-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedBranch === '1H' ? 'active' : 'bg-indigo-50 text-slate-600 hover:bg-indigo-100'}`}>
                  1H
                </button>
                <button className={`time-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedBranch === '4H' ? 'active' : 'bg-indigo-50 text-slate-600 hover:bg-indigo-100'}`}>
                  4H
                </button>
                <button className={`time-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedBranch === 'TODAY' ? 'active' : 'bg-indigo-50 text-slate-600 hover:bg-indigo-100'}`}>
                  TODAY
                </button>
                <button className={`time-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedBranch === 'ALL' ? 'active' : 'bg-indigo-50 text-slate-600 hover:bg-indigo-100'}`}>
                  ALL
                </button>
                <div className="w-px h-6 bg-slate-200 mx-1" />
                {branches.map((branch) => (
                  <button
                    key={branch.code}
                    className={`branch-btn px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${selectedBranch === branch.code ? 'active' : 'bg-indigo-50 text-slate-600 hover:bg-indigo-100'}`}
                    onClick={() => setSelectedBranch(branch.code)}
                  >
                    <span className={`branch-dot w-1.5 h-1.5 rounded-full ${branch.color}`} />
                    {branch.code}
                  </button>
                ))}
              </div>

              {/* Sales Trend Chart */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">PROPERTY SALES TREND — ALL LOCATIONS</h4>
                  <span className="text-xs font-bold text-slate-900">120 Properties</span>
                </div>
                <div className="flex items-end gap-0.5 h-24">
                  {trendData.map((value, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t-sm"
                      style={{
                        height: `${(value / 50) * 100}%`,
                        background: `linear-gradient(to top, ${i % 2 === 0 ? '#a5b4fc' : '#c4b5fd'}, ${i % 2 === 0 ? '#c7d2fe' : '#ddd6fe'})`,
                        opacity: 0.6 + (value / 50) * 0.4,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* Footer Navigation */}
          {/* <FadeInCard delay={750}>
            <div className="flex items-center justify-between bg-gradient-to-br from-white to-slate-50 border border-indigo-100/80 rounded-2xl p-4 shadow-sm">
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-600 font-semibold text-sm hover:shadow-md transition-all">
                <Building2 size={18} />
                Properties
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-indigo-50 text-slate-600 font-semibold text-sm transition-all">
                <Users size={18} />
                Clients
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-indigo-50 text-slate-600 font-semibold text-sm transition-all">
                <BarChart3 size={18} />
                Reports
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-indigo-50 text-slate-600 font-semibold text-sm transition-all">
                <MapPin size={18} />
                Locations
              </button>
            </div>
          </FadeInCard> */}

        </main>
      </div>
    </div>
  );
}
